// The agent-facing surface: four verbs over script.json.
//
//   read(addr?)      orient / read prose / inspect an asset — zoom by address
//   find(query)      locate by text, speaker or type -> anchors + match window
//   edit(ops)        surgical field-level changes on explicit anchors
//   write(addr,text) replace a scene from the same prose `read` emits
import { allActions, assetName, commit, load, newId, parse, resolve, save, stateName, } from "./model.js";
import { formatAction, formatScene, formatSceneHead, parseHead, parseLine, parseRefText, splitBlock, } from "./text.js";
import { step } from "./trace.js";
import { SPAN_COLUMNS, resolveSpan, spanAddresses, spanPoints } from "./span.js";
import { ACTION_TYPES, ACTION_TYPE_SET, IMPORTANCE_TIERS, IMPORTANCE_TIER_SET, ROLE_TYPES, ROLE_TYPE_SET, SCENE_SPACE_VALUES, SCENE_SPACE_SET, SCENE_TIME_VALUES, SCENE_TIME_SET, issuesIntroduced, parseTimestamp, TimestampParseError, validateScriptV3, validateScriptV4, } from "./schema/index.js";
export { load, parse, commit, save, resolve, allActions, assetName, stateName };
export { SPAN_COLUMNS, resolveSpan, spanPoints, spanAddresses } from "./span.js";
export { record, stop } from "./trace.js";
const NON_ACTOR_NAMES = ["旁白", "广播", "画外", "群声"];
const NON_ACTOR_KIND = {
    旁白: "system", 广播: "broadcast", 画外: "offscreen", 群声: "group",
};
/** Names accepted in a `NAME：` prefix — assets, their aliases, and `名字·限定`. */
export function speakerResolver(doc) {
    const names = new Set(NON_ACTOR_NAMES);
    for (const a of doc.assets) {
        names.add(a.name);
        for (const al of a.aliases)
            names.add(al);
    }
    // Try the whole label first: asset names legitimately contain `·`
    // (`篮球馆·更衣室`), so it is only a state qualifier when it has to be.
    return (raw) => names.has(raw) || names.has(raw.split("·")[0]);
}
export function read(doc, addr = "", opts = {}) {
    const node = resolve(doc, addr);
    step("read", `dispatch on node kind "${node.t}"`);
    switch (node.t) {
        case "script": return scriptCard(doc);
        case "ep": return readEpisode(doc, node.ep, opts);
        case "scene": return formatScene(doc, node.scene, opts);
        case "asset": return assetCard(doc, node.asset);
        case "state": return stateCard(doc, node.asset, node.state);
        case "action": {
            const n = opts.context ?? 3;
            const all = node.action.scene.actions;
            const i = all.indexOf(node.action);
            const slice = all.slice(Math.max(0, i - n), i + n + 1);
            const gutter = opts.plain ? 0 : Math.max(...slice.map((a) => a.anchor.length));
            const tp = node.action.raw.transition_prompt;
            const extra = Object.entries(node.action.raw.extend ?? {}).filter(([k]) => k !== "timestamp");
            return [
                formatSceneHead(doc, node.action.scene),
                ...slice.map((a) => (a === node.action ? "> " : "  ") + formatAction(doc, a, gutter, opts)),
                // Fields that have no place on the prose line still have to be visible
                // somewhere, or "editable" is a lie.
                tp ? `transition: ${assetName(doc, tp.target)} | ${tp.process} | ${tp.contrast}` : "",
                extra.length ? `extend: ${extra.map(([k, v]) => `${k}=${JSON.stringify(v)}`).join(", ")}` : "",
            ].filter(Boolean).join("\n");
        }
    }
}
function chars(s) { return s.actions.reduce((n, a) => n + a.content.length, 0); }
function scriptCard(doc) {
    const r = doc.raw;
    const scenes = doc.episodes.reduce((n, e) => n + e.scenes.length, 0);
    const actions = doc.episodes.reduce((n, e) => n + e.scenes.reduce((m, s) => m + s.actions.length, 0), 0);
    const out = [
        `${r.title || "(untitled)"}  v${doc.version}  ${doc.episodes.length} ep · ${scenes} scenes · ${actions} actions`,
    ];
    for (const k of ["logline", "theme", "synopsis", "worldview", "style"]) {
        if (r[k])
            out.push(`${k}: ${clip(String(r[k]), 300)}`);
    }
    if (Array.isArray(r.main_characters) && r.main_characters.length) {
        out.push(`main: ${r.main_characters.map((m) => `${m.name}=${m.role}`).join(", ")}`);
    }
    for (const kind of ["actor", "location", "prop"]) {
        const list = doc.assets.filter((a) => a.kind === kind);
        if (!list.length)
            continue;
        out.push("", `${kind}s (${list.length})`);
        for (const a of list.slice(0, 40)) {
            const al = a.aliases.length ? `  ~${a.aliases.join(",")}` : "";
            const imp = a.importance ? ` [${a.importance}]` : "";
            out.push(`  ${a.id}  ${a.name}${imp}  ${a.states.length}st${al}`);
        }
        if (list.length > 40)
            out.push(`  … ${list.length - 40} more`);
    }
    out.push("", "episodes");
    for (const e of doc.episodes) {
        const c = e.scenes.reduce((n, s) => n + chars(s), 0);
        out.push(`  ${e.no}  ${e.id}  ${e.scenes.length} scenes  ${c} chars${e.title ? `  ${e.title}` : ""}`
            + (e.synopsis ? `  ${clip(e.synopsis, 120)}` : ""));
    }
    return out.join("\n");
}
function readEpisode(doc, ep, opts) {
    const head = `# EP${String(ep.no).padStart(2, "0")}  ${ep.id}  ${ep.scenes.length} scenes`
        + (ep.title ? `  ${ep.title}` : "");
    if (opts.depth === "outline") {
        return [head, ...ep.scenes.map((s) => formatSceneHead(doc, s))].join("\n");
    }
    return [head, ...ep.scenes.map((s) => formatScene(doc, s, opts))].join("\n\n");
}
function assetCard(doc, a) {
    const role = a.raw.role_type ? `  <${a.raw.role_type}>` : "";
    const out = [`${a.id}  ${a.name}  ${a.kind}${a.importance ? `  [${a.importance}]` : ""}${role}`];
    if (a.aliases.length)
        out.push(`aliases: ${a.aliases.join(", ")}`);
    if (a.desc)
        out.push(a.desc);
    out.push(`states (${a.states.length})`);
    for (const s of a.states)
        out.push(`  ${s.id}  ${s.name}  ${clip(s.desc, 160)}`);
    const hits = [];
    const byState = new Map();
    for (const ep of doc.episodes)
        for (const s of ep.scenes) {
            const ref = [s.location, ...s.actors, ...s.props].find((r) => r && r.id === a.id);
            if (!ref)
                continue;
            hits.push(s);
            for (const st of ref.states)
                byState.set(st, (byState.get(st) ?? 0) + 1);
        }
    if (hits.length) {
        const eps = [...new Set(hits.map((s) => s.ep.no))];
        out.push(`appears: ${hits.length} scenes across ${eps.length} ep`
            + `  first ${hits[0].ep.no}-${hits[0].no}  last ${hits.at(-1).ep.no}-${hits.at(-1).no}`);
        out.push(`state usage: ${[...byState].map(([k, v]) => `${stateName(doc, a.id, k)}×${v}`).join(", ")}`);
        const unused = a.states.filter((s) => !byState.has(s.id));
        if (unused.length)
            out.push(`unused states: ${unused.map((s) => `${s.id}(${s.name})`).join(", ")}`);
    }
    else {
        out.push("appears: never referenced");
    }
    return out.join("\n");
}
function stateCard(doc, a, st) {
    const scenes = [];
    for (const ep of doc.episodes)
        for (const s of ep.scenes) {
            const ref = [s.location, ...s.actors, ...s.props].find((r) => r && r.id === a.id);
            if (ref?.states.includes(st.id))
                scenes.push(s);
        }
    const speakers = allActions(doc).filter((x) => x.speaker?.id === a.id && x.speaker.state === st.id);
    return [
        `${a.id}/${st.id}  ${st.name}  (${a.kind} ${a.name})`,
        st.desc,
        scenes.length
            ? `bound in ${scenes.length} scene(s): ${scenes.slice(0, 12).map((s) => `${s.ep.no}-${s.no}`).join(", ")}`
                + (scenes.length > 12 ? ` …` : "")
            : "bound in no scene",
        speakers.length ? `named by ${speakers.length} speaker reference(s)` : "",
    ].filter(Boolean).join("\n");
}
function clip(s, n) { return s.length > n ? s.slice(0, n) + "…" : s; }
function filterActions(doc, opts) {
    let pool = allActions(doc);
    step("find", `pool = all actions (${pool.length})`);
    if (opts.in) {
        const node = resolve(doc, opts.in);
        if (node.t === "ep")
            pool = node.ep.scenes.flatMap((s) => s.actions);
        else if (node.t === "scene")
            pool = node.scene.actions;
        else if (node.t === "action")
            pool = [node.action];
        else
            throw new Error(`--in must address an episode, scene or action (got ${node.t})`);
        step("find", `--in ${opts.in} narrows pool to ${pool.length}`);
    }
    if (opts.type) {
        pool = pool.filter((a) => a.type === opts.type);
        step("find", `--type ${opts.type} -> ${pool.length}`);
    }
    if (opts.speaker) {
        const who = resolve(doc, opts.speaker);
        if (who.t !== "asset")
            throw new Error(`--speaker must name an actor`);
        pool = pool.filter((a) => a.speaker?.id === who.asset.id);
        step("find", `--speaker ${opts.speaker} -> ${who.asset.id} -> ${pool.length}`);
    }
    return pool;
}
export function find(doc, query, opts = {}) {
    const pool = filterActions(doc, opts);
    const re = query ? new RegExp(query, "u") : null;
    const hits = pool.filter((a) => !re || re.test(a.content));
    step("find", `regex /${query}/ -> ${hits.length} hits`);
    const limit = opts.limit ?? 40;
    const w = opts.window ?? 16;
    const shown = hits.slice(0, limit);
    const gutter = Math.max(0, ...shown.map((a) => a.anchor.length));
    const head = `${hits.length} hit${hits.length === 1 ? "" : "s"}`
        + (query ? ` for /${query}/` : "") + (hits.length > shown.length ? `  (showing ${shown.length})` : "");
    const lines = shown.map((a) => {
        // v3 anchors already are the ordinal; don't print it twice.
        const ord = `${a.scene.ep.no}-${a.scene.no}-${a.no}`;
        const pos = ord === a.anchor ? "" : ord.padEnd(8) + "  ";
        const who = a.speaker?.id ? assetName(doc, a.speaker.id) + "：" : "";
        const body = opts.full || !re ? a.content : windowed(a.content, re, w);
        return `${a.anchor.padEnd(gutter)}  ${pos}${who}${body}`;
    });
    return [head, ...lines].join("\n");
}
function windowed(text, re, w) {
    const m = text.match(re);
    if (!m || m.index === undefined)
        return clip(text, w * 2);
    const a = Math.max(0, m.index - w), b = Math.min(text.length, m.index + m[0].length + w);
    return (a > 0 ? "…" : "") + text.slice(a, b) + (b < text.length ? "…" : "");
}
// ---- span --------------------------------------------------------------------
/**
 * The video window that covers a set of actions. `range` takes the same shapes
 * `find --in` does — an episode, a scene, one action, or `A..B` — so an agent
 * that just located a candidate can ask what to watch without a second syntax.
 */
export function span(doc, range) {
    const pool = filterActions(doc, { in: range });
    const row = resolveSpan(spanPoints(doc), spanAddresses(doc, pool));
    return SPAN_COLUMNS.map((c) => `${c}=${row[c]}`).join("  ");
}
const ACTION_FIELDS = ["content", "paren", "speaker", "ts", "type", "transition"];
const ASSET_FIELDS = ["name", "desc", "importance", "aliases", "role"];
const STATE_FIELDS = ["name", "desc"];
const EPISODE_FIELDS = ["title", "synopsis"];
const SCENE_FIELDS = ["space", "time"];
const SCRIPT_FIELDS = ["title", "worldview", "style", "synopsis", "logline", "theme", "main"];
function only(at, kind, fields, allowed) {
    const given = Object.keys(fields).filter((k) => fields[k] !== undefined);
    if (!given.length)
        throw new Error(`Nothing to change on ${at}; pass at least one field`);
    const bad = given.filter((k) => !allowed.includes(k));
    if (bad.length) {
        const art = /^[aeiou]/.test(kind) ? "an" : "a";
        throw new Error(`${at} is ${art} ${kind}; it has no ${bad.join(", ")}`
            + ` — ${art} ${kind} takes ${allowed.join(", ")}`);
    }
    return given;
}
/** Same verb for every node kind: the address decides which fields are legal. */
export function edit(doc, at, fields) {
    const node = resolve(doc, at);
    if (node.t === "asset")
        return editAsset(doc, at, node.asset, fields);
    if (node.t === "state")
        return editState(doc, at, node.asset, node.state, fields);
    if (node.t === "script")
        return editPlain(doc, at || "script", "script", node.doc.raw, fields, SCRIPT_FIELDS);
    if (node.t === "ep")
        return editPlain(doc, at, "episode", node.ep.raw, fields, EPISODE_FIELDS);
    if (node.t === "scene")
        return editScene(doc, at, node.scene, fields);
    const a = node.action;
    const raw = a.raw;
    const changed = only(a.anchor, "action", fields, ACTION_FIELDS);
    const base = baseline(doc);
    step("edit", `${a.anchor}: ${changed.join(", ")}`);
    if (fields.type !== undefined)
        raw.type = oneOf("type", fields.type, ACTION_TYPE_SET, ACTION_TYPES);
    if (fields.content !== undefined)
        raw.content = fields.content;
    if (fields.paren !== undefined)
        setParen(doc, raw, fields.paren);
    if (fields.speaker !== undefined)
        raw.speaker = speakerJson(doc, fields.speaker);
    if (fields.ts !== undefined)
        setTs(raw, fields.ts);
    if (fields.transition !== undefined) {
        if (fields.transition === null)
            delete raw.transition_prompt;
        else {
            const [target, process, contrast] = fields.transition.split("|").map((x) => x.trim());
            if (!target || !process || !contrast) {
                throw new Error(`transition takes "target|process|contrast" (got "${fields.transition}")`);
            }
            const t = resolve(doc, target);
            if (t.t !== "asset")
                throw new Error(`transition target must be an asset (got ${target})`);
            raw.transition_prompt = { target: t.asset.id, process, contrast };
        }
    }
    save(doc);
    return `${a.anchor}  ${changed.join("+")}  ${preview(raw)}` + since(doc, base);
}
/** Plain string metadata on the script root or an episode. */
function editPlain(doc, at, kind, raw, fields, allowed) {
    const changed = only(at, kind, fields, allowed);
    const base = baseline(doc);
    for (const k of changed) {
        if (k === "main") {
            raw.main_characters = fields.main.split(",").map((pair) => {
                const [name, role] = pair.split("=").map((x) => x.trim());
                if (!name || !role)
                    throw new Error(`main takes "名字=角色定位,…" (got "${pair}")`);
                return { name, role };
            });
        }
        else if (k === "aliases" || k === "role") {
            throw new Error(`${at} is ${kind}; it has no ${k}`);
        }
        else {
            raw[k] = fields[k];
        }
    }
    step("edit", `${kind} ${at}: ${changed.join(", ")}`);
    save(doc);
    return `${at}  ${changed.join("+")}` + since(doc, base);
}
function editScene(doc, at, scene, fields) {
    const changed = only(at, "scene", fields, SCENE_FIELDS);
    const base = baseline(doc);
    const env = { ...(scene.raw.environment ?? {}) };
    if (fields.space !== undefined)
        env.space = oneOf("space", fields.space, SCENE_SPACE_SET, SCENE_SPACE_VALUES);
    if (fields.time !== undefined)
        env.time = oneOf("time", fields.time, SCENE_TIME_SET, SCENE_TIME_VALUES);
    scene.raw.environment = env;
    step("edit", `scene ${scene.anchor}: ${changed.join(", ")}`);
    save(doc);
    return `${scene.anchor}  ${changed.join("+")}  ${[env.space, env.time].filter(Boolean).join("/")}`
        + since(doc, base);
}
function editAsset(doc, at, a, fields) {
    const changed = only(at, "asset", fields, ASSET_FIELDS);
    const base = baseline(doc);
    if (fields.name !== undefined) {
        const clash = doc.assets.find((x) => x !== a && (x.name === fields.name || x.aliases.includes(fields.name)));
        if (clash)
            throw new Error(`"${fields.name}" already names ${clash.kind} ${clash.id}`);
        a.raw[`${a.kind}_name`] = fields.name;
    }
    if (fields.desc !== undefined)
        a.raw.description = fields.desc;
    if (fields.importance !== undefined)
        a.raw.importance = checkImportance(fields.importance);
    if (fields.role !== undefined) {
        if (a.kind !== "actor")
            throw new Error(`role only applies to actors (${a.name} is a ${a.kind})`);
        if (fields.role)
            a.raw.role_type = oneOf("role", fields.role, ROLE_TYPE_SET, ROLE_TYPES);
        else
            delete a.raw.role_type;
    }
    if (fields.aliases !== undefined) {
        if (fields.aliases.length)
            a.raw.aliases = fields.aliases;
        else
            delete a.raw.aliases;
    }
    step("edit", `asset ${a.id}: ${changed.join(", ")}`);
    save(doc);
    return `${a.id}  ${changed.join("+")}  ${fields.name ?? a.name}` + since(doc, base);
}
function editState(doc, at, a, st, fields) {
    const changed = only(at, "state", fields, STATE_FIELDS);
    const base = baseline(doc);
    const raw = a.raw.states.find((x) => x.state_id === st.id);
    if (fields.name !== undefined) {
        if (a.states.some((x) => x.id !== st.id && x.name === fields.name)) {
            throw new Error(`${a.name} already has a state named "${fields.name}"`);
        }
        raw.state_name = fields.name;
    }
    if (fields.desc !== undefined)
        raw.description = fields.desc;
    step("edit", `state ${a.id}/${st.id}: ${changed.join(", ")}`);
    save(doc);
    return `${a.id}/${st.id}  ${changed.join("+")}  ${fields.name ?? st.name}` + since(doc, base);
}
/**
 * Insert one action next to an existing one. This is the narrow half of what
 * `write` could do: `write` replaces a whole scene from prose and can therefore
 * rewrite dialogue wholesale, which a fidelity review must not do. Adding a
 * single missing beat needs none of that power.
 */
export function insert(doc, at, where, type, content, speaker, paren) {
    const node = resolve(doc, at);
    if (node.t !== "action")
        throw new Error(`${at} is a ${node.t}; insert needs a sibling action`);
    const anchor = node.action;
    const base = baseline(doc);
    const raw = { type: oneOf("type", type, ACTION_TYPE_SET, ACTION_TYPES), content };
    if (doc.version === 4)
        raw.action_id = newId("ac", new Set(doc.actions.keys()));
    if (type === "dialogue" || type === "inner_thought") {
        if (!speaker)
            throw new Error(`a ${type} needs a speaker`);
        raw.speaker = speakerJson(doc, speaker);
        if (paren !== undefined)
            setParen(doc, raw, paren);
    }
    else if (speaker || paren) {
        throw new Error(`a ${type} takes neither speaker nor paren`);
    }
    const list = anchor.scene.raw.actions;
    const index = list.indexOf(anchor.raw) + (where === "before" ? 0 : 1);
    list.splice(index, 0, raw);
    step("insert", `${raw.action_id ?? `${anchor.scene.anchor}#${index}`} ${where} ${anchor.anchor}`);
    save(doc);
    return `${raw.action_id ?? `${anchor.scene.anchor}#${index}`}  ${where} ${anchor.anchor}`
        + `  ${preview(raw)}` + since(doc, base);
}
/**
 * Relocate one action. This is the single thing `write` cannot do: it works a
 * scene at a time, so moving across scenes through two writes drops the action
 * from one and creates a *new* id in the other. Here the identity survives.
 */
export function move(doc, at, to, where = "after") {
    const src = resolve(doc, at);
    if (src.t !== "action")
        throw new Error(`${at} is a ${src.t}; move targets one action`);
    const dst = resolve(doc, to);
    if (dst.t !== "action")
        throw new Error(`${to} is a ${dst.t}; move --to takes a sibling action`);
    if (src.action === dst.action)
        throw new Error(`${at} cannot move relative to itself`);
    const base = baseline(doc);
    const from = src.action.scene.raw.actions;
    from.splice(from.indexOf(src.action.raw), 1);
    const into = dst.action.scene.raw.actions;
    into.splice(into.indexOf(dst.action.raw) + (where === "before" ? 0 : 1), 0, src.action.raw);
    step("move", `${src.action.scene.anchor} -> ${dst.action.scene.anchor}, identity ${src.action.anchor} preserved`);
    save(doc);
    return `${src.action.anchor}  ${where} ${dst.action.anchor}`
        + `  (${src.action.scene.anchor} -> ${dst.action.scene.anchor})` + since(doc, base);
}
function setParen(doc, raw, v) {
    const key = doc.version === 4 ? "parenthetical" : "emotion";
    if (v === null)
        delete raw[key];
    else
        raw[key] = v;
}
// Timestamps are parsed and stored canonically — `0:07` becomes `00:07`, and an
// unparseable value is refused here rather than surfacing later as a validation
// issue on a file that already holds it. Sub-second precision is preserved as
// written, because it is meaningful for video alignment.
function setTs(raw, v) {
    if (v === null) {
        if (raw.extend)
            delete raw.extend.timestamp;
        return;
    }
    let canonical;
    try {
        canonical = parseTimestamp(v).canonical;
    }
    catch (error) {
        if (!(error instanceof TimestampParseError))
            throw error;
        throw new Error(`${error.message} (got ${JSON.stringify(v)})`);
    }
    raw.extend = { ...(raw.extend ?? {}), timestamp: canonical };
}
function speakerJson(doc, name) {
    const whole = doc.assets.find((a) => a.name === name || a.aliases.includes(name));
    const [base, qual] = whole ? [name, undefined] : splitOnce(name, "·");
    const kind = NON_ACTOR_KIND[base];
    if (kind)
        return qual ? { kind, label: qual } : { kind };
    const n = resolve(doc, base);
    if (n.t !== "asset" || n.asset.kind !== "actor")
        throw new Error(`Not an actor: ${name}`);
    const out = { actor_id: n.asset.id };
    if (qual) {
        const st = n.asset.states.find((s) => s.name === qual || s.id === qual);
        if (!st)
            throw new Error(`Unknown state "${qual}" on ${n.asset.name}`);
        out.state_id = st.id;
    }
    return out;
}
function splitOnce(v, sep) {
    const i = v.indexOf(sep);
    return i < 0 ? [v, undefined] : [v.slice(0, i), v.slice(i + sep.length)];
}
function preview(raw) { return clip(String(raw.content ?? ""), 40); }
/**
 * Replace a scene or a whole episode from the prose `read` prints.
 *
 * Diffing is by anchor at every level: a line or `##` header carrying a known
 * anchor keeps its identity, one without an anchor is created, and an anchor
 * absent from the text is deleted. That last rule is the only thing about this
 * format an agent cannot infer from reading one output, so deleting a *scene*
 * needs `allowDelete` — losing 30 actions to a forgotten header is not a
 * mistake we let happen quietly.
 *
 * `addr` may also be `"+"` or the next episode ordinal, which appends a new one.
 */
export function write(doc, addr, text, opts = {}) {
    const draft = parse(structuredClone(doc.raw));
    const result = writeDraft(draft, addr, text, opts);
    doc.raw = draft.raw;
    save(doc);
    return result;
}
function writeDraft(doc, addr, text, opts) {
    if (addr === "+" || Number(addr) === doc.episodes.length + 1) {
        const raw = { scenes: [] };
        if (doc.version === 4)
            raw.episode_id = newId("ep", new Set(doc.eps.keys()));
        doc.raw.episodes.push(raw);
        step("write", `appending episode ${doc.episodes.length + 1}`);
        return writeEpisodeRaw(doc, raw, doc.episodes.length + 1, text, opts, true);
    }
    const node = resolve(doc, addr);
    if (node.t === "scene")
        return writeScene(doc, node.scene, text, opts);
    if (node.t === "ep")
        return writeEpisodeRaw(doc, node.ep.raw, node.ep.no, text, opts, false);
    throw new Error(`write targets a scene or an episode (got ${node.t})`);
}
function writeScene(doc, scene, text, opts) {
    const base = baseline(doc);
    const r = applyScene(doc, scene.raw, scene, text, new Set(doc.actions.keys()), opts);
    save(doc);
    return report(scene.anchor, r) + since(doc, base);
}
function report(label, r) {
    return `${label}: ${r.total} actions (${r.kept} kept, ${r.created} new, ${r.dropped.length} deleted)`
        + (r.dropped.length
            ? `\ndeleted: ${r.dropped.map((a) => `${a.anchor} ${preview(a.raw)}`).join("\n         ")}`
            : "");
}
/** Rewrite one scene's header fields and action list from prose. */
function applyScene(doc, raw, prev, block, taken, opts, pool, claimed) {
    const known = speakerResolver(doc);
    const { head: headLine, meta: metaLine, body: lines } = splitBlock(block.split("\n"));
    if (headLine) {
        const h = parseHead(headLine, metaLine);
        raw.environment = h.env.space || h.env.time ? h.env : undefined;
        if (raw.environment === undefined)
            delete raw.environment;
        raw.location = h.location ? refJson(doc, h.location, "location") : null;
        raw.actors = h.cast.map((c) => refJson(doc, c, "actor"));
        raw.props = h.props.map((p) => refJson(doc, p, "prop"));
    }
    // At episode scope the pool spans every scene of the episode, so an anchor may
    // legitimately land in a different scene: that is how splitting and merging
    // scenes is expressed, without a separate verb.
    const byAnchor = pool ?? new Map((prev?.actions ?? []).map((a) => [a.anchor, a]));
    const seen = claimed ?? new Set();
    const kept = new Set();
    const out = [];
    let created = 0;
    const label = prev ? prev.anchor : "this new scene";
    for (const line of lines) {
        const p = parseLine(line, known);
        if (!p)
            continue;
        const old = p.anchor ? byAnchor.get(p.anchor) : undefined;
        if (p.anchor && !old) {
            throw new Error(`Anchor ${p.anchor} is not in ${label}`
                + ` — move it with \`move\`, or drop the anchor to create a new action`);
        }
        // One anchor is one action. Repeating it would put the same object in the
        // array twice: a duplicate id on disk, and two lines that edit each other.
        if (old && seen.has(old.anchor)) {
            throw new Error(`Anchor ${p.anchor} appears twice in ${label}`
                + ` — drop the anchor from the copy to make it a new action`);
        }
        const node = old ? old.raw : {};
        if (!old && doc.version === 4)
            node.action_id = newId("ac", taken);
        node.type = oneOf("type", p.type, ACTION_TYPE_SET, ACTION_TYPES);
        node.content = p.content;
        if (p.type === "dialogue" || p.type === "inner_thought") {
            node.speaker = speakerJson(doc, p.speakerName);
            setParen(doc, node, p.paren ?? null);
        }
        else {
            delete node.speaker;
            setParen(doc, node, null);
        }
        setTs(node, p.ts ?? null);
        if (old) {
            kept.add(old.anchor);
            seen.add(old.anchor);
        }
        else
            created++;
        out.push(node);
    }
    // An empty body is overwhelmingly a forgotten paste, not an intentional wipe
    // — the same reason deleting a whole scene needs the flag. Scenes that were
    // already empty are not a deletion and pass through untouched; real scripts
    // do contain them.
    if (!out.length && (prev?.actions.length ?? 0) > 0 && !opts.allowDelete) {
        throw new Error(`That would leave ${label} with no actions`
            + `${prev ? ` (deleting all ${prev.actions.length})` : ""}`
            + ` — the body after the \`##\` header is empty; pass --allow-delete if that is the intent`);
    }
    // Not one line matched an existing anchor: this is `--plain` output, or text
    // from another scene. Writing it would replace every action id in place.
    if (out.length && !kept.size && (prev?.actions.length ?? 0) > 0 && !opts.allowDelete) {
        throw new Error(`None of the ${out.length} line(s) carry an anchor from ${label},`
            + ` so all ${prev.actions.length} existing action(s) would be replaced by new ids`
            + ` — \`read --plain\` output cannot be written back; re-read without --plain,`
            + ` or pass --allow-delete to rewrite the scene from scratch`);
    }
    raw.actions = out;
    return { total: out.length, kept: kept.size, created,
        dropped: pool ? [] : (prev?.actions ?? []).filter((a) => !kept.has(a.anchor)) };
}
function writeEpisodeRaw(doc, epRaw, epNo, text, opts, isNew) {
    const base = baseline(doc);
    const prevScenes = isNew ? [] : doc.episodes[epNo - 1].scenes;
    const byAnchor = new Map();
    for (const s of prevScenes) {
        byAnchor.set(s.anchor, s);
        byAnchor.set(s.id, s);
    }
    // Split on `##`; anything before the first header (an `# EP..` title line) is
    // preamble and carries no scene.
    const blocks = [];
    for (const line of text.split("\n")) {
        if (line.startsWith("##"))
            blocks.push(line);
        else if (blocks.length)
            blocks[blocks.length - 1] += "\n" + line;
    }
    if (!blocks.length)
        throw new Error(`No scene headers found — an episode body needs \`## …\` lines`);
    const takenAc = new Set(doc.actions.keys());
    const takenScn = new Set(doc.scenes.keys());
    const pool = new Map();
    for (const s of prevScenes)
        for (const a of s.actions)
            pool.set(a.anchor, a);
    const claimed = new Set();
    const seen = new Set();
    const scenes = [];
    const parts = [];
    let newScenes = 0;
    for (const block of blocks) {
        const h = parseHead(block.split("\n")[0]);
        const prev = h.anchor ? byAnchor.get(h.anchor) : undefined;
        if (h.anchor && !prev) {
            throw new Error(`Scene ${h.anchor} is not in episode ${epNo}`
                + ` — drop the id from the header to create a new scene here`);
        }
        const raw = prev ? prev.raw : {};
        if (!prev) {
            if (doc.version === 4)
                raw.scene_id = newId("scn", takenScn);
            else
                raw.scene_id = `scn_${String(prevScenes.length + newScenes + 1).padStart(3, "0")}`;
            newScenes++;
        }
        else
            seen.add(prev.anchor);
        const r = applyScene(doc, raw, prev ?? null, block, takenAc, opts, pool, claimed);
        scenes.push(raw);
        parts.push(report(prev ? prev.anchor : `new ${raw.scene_id}`, r));
    }
    const goneActions = [...pool.values()].filter((a) => !claimed.has(a.anchor));
    if (goneActions.length && !opts.allowDelete) {
        throw new Error(`This would delete ${goneActions.length} action(s) from episode ${epNo}: `
            + goneActions.slice(0, 6).map((a) => `${a.anchor}(${a.scene.anchor})`).join(", ")
            + (goneActions.length > 6 ? ` …` : "")
            + ` — keep their lines, or pass --allow-delete if that is the intent`);
    }
    const goneScenes = prevScenes.filter((s) => !seen.has(s.anchor));
    if (goneScenes.length && !opts.allowDelete) {
        throw new Error(`This would delete ${goneScenes.length} whole scene(s) from episode ${epNo}: `
            + goneScenes.map((s) => `${s.anchor}(${s.actions.length} actions)`).join(", ")
            + ` — include their \`##\` headers to keep them, or pass --allow-delete if that is the intent`);
    }
    step("write", `episode ${epNo}: ${blocks.length} scene blocks, ${newScenes} new, ${goneScenes.length} deleted`);
    epRaw.scenes = scenes;
    save(doc);
    return `EP${epNo}: ${scenes.length} scenes (${scenes.length - newScenes} kept, ${newScenes} new,`
        + ` ${goneScenes.length} deleted, ${goneActions.length} actions deleted)\n` + parts.map((p) => "  " + p.replace(/\n/g, "\n  ")).join("\n")
        + (goneScenes.length ? `\ndeleted scenes: ${goneScenes.map((s) => s.anchor).join(", ")}` : "")
        + since(doc, base);
}
/** `名字` or `名字(状态+状态)` -> the JSON reference, resolving names to ids. */
function refJson(doc, text, kind) {
    const { name, states } = parseRefText(text);
    const n = resolve(doc, name);
    if (n.t !== "asset" || n.asset.kind !== kind) {
        throw new Error(`"${name}" is not a registered ${kind} — create it with \`asset\` first`);
    }
    const ids = (states.length ? states : ["default"]).map((sn) => {
        const hit = n.asset.states.filter((st) => st.name === sn || st.id === sn);
        if (!hit.length) {
            throw new Error(`${n.asset.name} has no state "${sn}" (has: ${n.asset.states.map((x) => x.name).join(", ")})`);
        }
        if (hit.length > 1)
            throw new Error(`${n.asset.name} has ${hit.length} states named "${sn}"; use the state id`);
        return hit[0].id;
    });
    const uniq = [...new Set(ids)].sort();
    const key = `${kind}_id`;
    return doc.version === 4
        ? { [key]: n.asset.id, state_ids: uniq }
        : { [key]: n.asset.id, state_id: uniq[0] };
}
// ---- ref -------------------------------------------------------------------
/**
 * Set, change or remove one scene's reference to one asset — the atom behind a
 * `cast:` / `props:` / location entry in a scene header.
 *
 * `states` is the complete set for that asset in that scene, not an append:
 * passing it replaces whatever was bound. Nothing is implied, so binding
 * `default` means saying `default`. A bulk change across a whole episode is a
 * `write` of that episode's headers, not a flag here.
 */
export function ref(doc, sceneAddr, assetAddr, states, remove = false) {
    const node = resolve(doc, sceneAddr);
    if (node.t !== "scene")
        throw new Error(`${sceneAddr} is a ${node.t}; ref targets one scene`);
    const scene = node.scene;
    const target = resolve(doc, assetAddr);
    if (target.t !== "asset")
        throw new Error(`${assetAddr} is a ${target.t}; ref takes an actor, location or prop`);
    const a = target.asset;
    const field = a.kind === "actor" ? "actors" : a.kind === "prop" ? "props" : "location";
    const base = baseline(doc);
    if (remove) {
        if (a.kind === "location") {
            if (scene.location?.id !== a.id) {
                throw new Error(`${scene.anchor}'s location is `
                    + `${scene.location ? assetName(doc, scene.location.id) : "empty"}, not ${a.name}`);
            }
            scene.raw.location = null;
        }
        else {
            const list = scene.raw[field];
            const i = list.findIndex((r) => r[`${a.kind}_id`] === a.id);
            if (i < 0)
                throw new Error(`${scene.anchor} does not reference ${a.name}`);
            list.splice(i, 1);
        }
        step("ref", `${scene.anchor} -= ${a.id}`);
        save(doc);
        return `${scene.anchor}  removed ${a.name}` + since(doc, base);
    }
    if (!states.length) {
        throw new Error(`ref needs at least one state for ${a.name}`
            + ` (has: ${a.states.map((st) => st.name).join(", ")}) — nothing is implied, not even default`);
    }
    const ids = [...new Set(states.map((sn) => {
            const hit = a.states.filter((st) => st.name === sn || st.id === sn);
            if (!hit.length) {
                throw new Error(`${a.name} has no state "${sn}" (has: ${a.states.map((x) => x.name).join(", ")})`);
            }
            if (hit.length > 1)
                throw new Error(`${a.name} has ${hit.length} states named "${sn}"; use the state id`);
            return hit[0].id;
        }))].sort();
    const key = `${a.kind}_id`;
    const entry = doc.version === 4 ? { [key]: a.id, state_ids: ids } : { [key]: a.id, state_id: ids[0] };
    if (doc.version === 3 && ids.length > 1) {
        throw new Error(`Script v3 holds one state per reference; pass a single state`);
    }
    let verb;
    if (a.kind === "location") {
        verb = scene.location ? (scene.location.id === a.id ? "restated" : "replaced") : "set";
        scene.raw.location = entry;
    }
    else {
        const list = scene.raw[field];
        const i = list.findIndex((r) => r[key] === a.id);
        if (i < 0) {
            list.push(entry);
            verb = "added";
        }
        else {
            list[i] = entry;
            verb = "restated";
        }
    }
    step("ref", `${scene.anchor} ${verb} ${a.id} = [${ids.join(", ")}]`);
    save(doc);
    return `${scene.anchor}  ${verb} ${a.name}(${ids.map((i2) => stateName(doc, a.id, i2)).join("+")})`
        + since(doc, base);
}
/**
 * Substitute text across every action a `find` would match. Same filter names
 * as `find` on purpose — this is "find, but substituting", so there is no
 * second selector language to learn.
 *
 * Dry-run by default: it prints the exact before/after lines and only writes
 * when `apply` is set. That, not a plan digest, is what keeps a 200-hit rewrite
 * reviewable.
 */
export function replace(doc, query, to, opts = {}) {
    if (!query)
        throw new Error("replace needs a pattern; an empty one would rewrite every action");
    const re = new RegExp(query, "gu");
    const base = baseline(doc);
    const pool = filterActions(doc, opts);
    const hits = pool.filter((a) => { re.lastIndex = 0; return re.test(a.content); });
    step("replace", `/${query}/ matched ${hits.length} of ${pool.length} actions in scope`);
    if (!hits.length)
        return `0 hits for /${query}/ — nothing to do`;
    const gutter = Math.max(...hits.map((a) => a.anchor.length));
    const lines = hits.map((a) => {
        const next = a.content.replace(re, to);
        return `${a.anchor.padEnd(gutter)}  ${a.scene.ep.no}-${a.scene.no}-${a.no}\n`
            + `${" ".repeat(gutter)}  - ${a.content}\n${" ".repeat(gutter)}  + ${next}`;
    });
    if (!opts.apply) {
        return `${hits.length} action(s) would change (dry run — pass apply to write)\n` + lines.join("\n");
    }
    for (const a of hits) {
        re.lastIndex = 0;
        a.raw.content = a.content.replace(re, to);
    }
    save(doc);
    step("replace", `wrote ${hits.length} actions`);
    return `${hits.length} action(s) changed` + since(doc, base) + "\n" + lines.join("\n");
}
// ---- asset -----------------------------------------------------------------
// Closed sets come from the schema package — the same constants the validator
// uses. Rejecting here beats writing a file that only `validate` will reject.
function oneOf(what, v, set, allowed) {
    if (!set.has(v))
        throw new Error(`${what} must be one of ${allowed.join(" | ")} (got "${v}")`);
    return v;
}
function checkImportance(v) {
    return oneOf("importance", v, IMPORTANCE_TIER_SET, IMPORTANCE_TIERS);
}
// ---- validation --------------------------------------------------------------
export function validateDoc(doc) {
    return doc.version === 4 ? validateScriptV4(doc.raw) : validateScriptV3(doc.raw);
}
/** Human-readable validation report. */
export function validate(doc) {
    const r = validateDoc(doc);
    const head = `${doc.episodes.length} episodes / ${doc.scenes.size ? [...new Set(doc.episodes.flatMap((e) => e.scenes))].length : 0} scenes`
        + ` / ${allActions(doc).length} actions — ${r.ok ? "ok" : `${r.errors.length} error(s)`}`
        + (r.warnings.length ? `, ${r.warnings.length} warning(s)` : "");
    return [head, ...fmtIssues([...r.errors.map((i) => ["error", i]),
            ...r.warnings.map((i) => ["warning", i])])].join("\n");
}
export function issues(doc, opts = {}) {
    const r = validateDoc(doc);
    let rows = [
        ...r.errors.map((i) => ["error", i]), ...r.warnings.map((i) => ["warning", i])
    ];
    if (opts.severity)
        rows = rows.filter(([s]) => s === opts.severity);
    if (opts.code)
        rows = rows.filter(([, i]) => i.code === opts.code);
    step("issues", `${rows.length} matching issue(s)`);
    return rows.length ? fmtIssues(rows).join("\n") : "No issues.";
}
function fmtIssues(rows) {
    return rows.map(([sev, i]) => `${sev} ${i.code}  ${i.path}  ${i.message}`
        + (i.repair_hint ? `\n      fix: ${i.repair_hint}` : ""));
}
// Every mutation reports what it broke that was not already broken. This
// replaces the timeline and speaker-state checks that used to be written by
// hand here: one rule set, defined once, in the schema package.
function baseline(doc) { return validateDoc(doc); }
function since(doc, base) {
    const now = validateDoc(doc);
    const fresh = [
        ...issuesIntroduced(base.errors, now.errors).map((i) => ["error", i]),
        ...issuesIntroduced(base.warnings, now.warnings).map((i) => ["warning", i]),
    ];
    if (!fresh.length)
        return "";
    step("check", `${fresh.length} issue(s) introduced by this edit`);
    return `\nintroduced ${fresh.length} issue(s):\n  ` + fmtIssues(fresh).join("\n  ");
}
const ASSET_FIELD = { actor: "actors", location: "locations", prop: "props" };
const ID_PREFIX = { actor: "act", location: "loc", prop: "prp" };
/**
 * Register a new actor, location or prop, with its `default` state. Assets must
 * exist before a scene header can reference them, so this is the entry point for
 * writing an episode from scratch.
 */
export function asset(doc, kind, name, desc, importance = "featured", aliases = []) {
    if (!name.trim())
        throw new Error("asset needs a name");
    const base = baseline(doc);
    const clash = doc.assets.find((a) => a.name === name || a.aliases.includes(name));
    if (clash)
        throw new Error(`"${name}" already names ${clash.kind} ${clash.id}; read it instead`);
    const field = ASSET_FIELD[kind];
    const id = doc.version === 4
        ? newId(ID_PREFIX[kind], new Set(doc.assets.map((a) => a.id)))
        : `${ID_PREFIX[kind]}_${String(doc.raw[field].length + 1).padStart(3, "0")}`;
    const raw = {
        [`${kind}_id`]: id, [`${kind}_name`]: name, importance: checkImportance(importance), description: desc,
        states: [{ state_id: "default", state_name: name, description: desc }],
    };
    if (aliases.length)
        raw.aliases = aliases;
    doc.raw[field].push(raw);
    step("asset", `${kind} ${id} "${name}" + default state`);
    save(doc);
    return `${id}  ${name}  ${kind}  states: default` + since(doc, base);
}
/** Add a visual state to an existing asset. */
export function state(doc, assetName, name, desc) {
    const n = resolve(doc, assetName);
    if (n.t !== "asset")
        throw new Error(`${assetName} is a ${n.t}; state targets an asset`);
    const base = baseline(doc);
    if (n.asset.states.some((s) => s.name === name)) {
        throw new Error(`${n.asset.name} already has a state named "${name}"`);
    }
    const id = doc.version === 4
        ? newId("st", new Set(n.asset.states.map((s) => s.id)))
        : `st_${String(n.asset.states.length).padStart(3, "0")}`;
    n.asset.raw.states.push({ state_id: id, state_name: name, description: desc });
    step("state", `${n.asset.id} + ${id} "${name}"`);
    save(doc);
    return `${n.asset.id}/${id}  ${name}  (${n.asset.name} now has ${n.asset.states.length + 1} states)`
        + since(doc, base);
}
// ---- ext / del / merge -----------------------------------------------------
function rawOf(doc, addr) {
    const n = resolve(doc, addr);
    switch (n.t) {
        case "script": return { raw: n.doc.raw, label: "script" };
        case "ep": return { raw: n.ep.raw, label: n.ep.id };
        case "scene": return { raw: n.scene.raw, label: n.scene.anchor };
        case "action": return { raw: n.action.raw, label: n.action.anchor };
        case "asset": return { raw: n.asset.raw, label: n.asset.id };
        case "state": {
            const raw = n.asset.raw.states.find((s) => s.state_id === n.state.id);
            return { raw, label: `${n.asset.id}/${n.state.id}` };
        }
    }
}
/**
 * Read or write `extend` on any node. Extension data is deliberately absent
 * from the prose view — it is arbitrary and would be noise on every line — so
 * this is how it stays reachable rather than merely preserved.
 */
export function ext(doc, addr, key, value, remove = false) {
    const { raw, label } = rawOf(doc, addr);
    const map = raw.extend ?? {};
    if (!key) {
        const keys = Object.keys(map);
        return keys.length
            ? `${label} extend:\n` + keys.map((k) => `  ${k} = ${JSON.stringify(map[k])}`).join("\n")
            : `${label} has no extend data`;
    }
    if (remove) {
        if (!(key in map))
            throw new Error(`${label} has no extend key "${key}"`);
        delete map[key];
        if (!Object.keys(map).length)
            delete raw.extend;
        else
            raw.extend = map;
        step("ext", `${label} -= ${key}`);
        save(doc);
        return `${label}  removed extend.${key}`;
    }
    if (value === undefined) {
        if (!(key in map))
            throw new Error(`${label} has no extend key "${key}"`);
        return `${label}  extend.${key} = ${JSON.stringify(map[key])}`;
    }
    raw.extend = { ...map, [key]: value };
    step("ext", `${label} extend.${key} = ${value}`);
    save(doc);
    return `${label}  extend.${key} = ${JSON.stringify(value)}`;
}
/**
 * Delete a node. The address decides what: action, scene, episode, asset or
 * state. Anything that other nodes point at refuses to vanish silently —
 * either rewire it with `replacement`, or say `force` and read the report.
 */
export function del(doc, addr, opts = {}) {
    const n = resolve(doc, addr);
    const base = baseline(doc);
    if (n.t === "action") {
        const list = n.action.scene.raw.actions;
        list.splice(list.indexOf(n.action.raw), 1);
        step("del", `action ${n.action.anchor} from ${n.action.scene.anchor}`);
        save(doc);
        return `deleted ${n.action.anchor}  ${preview(n.action.raw)}` + since(doc, base);
    }
    if (n.t === "scene") {
        if (n.scene.actions.length && !opts.force) {
            throw new Error(`${n.scene.anchor} still holds ${n.scene.actions.length} action(s)`
                + ` — pass --force to delete the scene and everything in it`);
        }
        const list = n.scene.ep.raw.scenes;
        list.splice(list.indexOf(n.scene.raw), 1);
        step("del", `scene ${n.scene.anchor} (${n.scene.actions.length} actions)`);
        save(doc);
        return `deleted scene ${n.scene.anchor} with ${n.scene.actions.length} action(s)` + since(doc, base);
    }
    if (n.t === "ep") {
        const acts = n.ep.scenes.reduce((m, s) => m + s.actions.length, 0);
        if (n.ep.scenes.length && !opts.force) {
            throw new Error(`episode ${n.ep.no} still holds ${n.ep.scenes.length} scene(s) / ${acts} action(s)`
                + ` — pass --force to delete the episode and everything in it`);
        }
        const list = doc.raw.episodes;
        list.splice(list.indexOf(n.ep.raw), 1);
        step("del", `episode ${n.ep.no}`);
        save(doc);
        return `deleted episode ${n.ep.no} (${n.ep.id}) with ${n.ep.scenes.length} scene(s) / ${acts} action(s)`;
    }
    if (n.t === "asset")
        return delAsset(doc, n.asset, opts) + since(doc, base);
    if (n.t === "state")
        return delState(doc, n.asset, n.state, opts) + since(doc, base);
    throw new Error(`del takes an action, scene, episode, asset or state (got ${n.t})`);
}
/** Every place a scene or a speaker points at this asset. */
function assetUses(doc, id) {
    const scenes = [];
    for (const ep of doc.episodes)
        for (const s of ep.scenes) {
            if ([s.location, ...s.actors, ...s.props].some((r) => r && r.id === id))
                scenes.push(s);
        }
    return { scenes, speakers: allActions(doc).filter((a) => a.speaker?.id === id) };
}
function delAsset(doc, a, opts) {
    const { scenes, speakers } = assetUses(doc, a.id);
    const used = scenes.length + speakers.length;
    if (used && !opts.replacement && !opts.force) {
        throw new Error(`${a.name} is still referenced by ${scenes.length} scene(s)`
            + ` and ${speakers.length} speaker(s) — merge it into another asset with`
            + ` \`merge ${a.id} <other>\`, or pass --force to strip every reference`);
    }
    if (opts.replacement)
        return merge(doc, a.id, opts.replacement);
    for (const s of scenes) {
        if (s.raw.location?.[`${a.kind}_id`] === a.id)
            s.raw.location = null;
        for (const f of ["actors", "props"]) {
            const list = s.raw[f];
            if (!list)
                continue;
            const i = list.findIndex((r) => r[`${a.kind}_id`] === a.id);
            if (i >= 0)
                list.splice(i, 1);
        }
    }
    for (const act of speakers)
        delete act.raw.speaker;
    const field = ASSET_FIELD[a.kind];
    const list = doc.raw[field];
    list.splice(list.indexOf(a.raw), 1);
    step("del", `asset ${a.id}, stripped ${scenes.length} scene refs + ${speakers.length} speakers`);
    save(doc);
    return `deleted ${a.kind} ${a.id} ${a.name}`
        + (used ? `\nstripped ${scenes.length} scene reference(s) and ${speakers.length} speaker(s)` : "");
}
function delState(doc, a, st, opts) {
    if (st.id === "default")
        throw new Error(`every asset keeps its default state; delete the asset instead`);
    const scenes = doc.episodes.flatMap((e) => e.scenes).filter((s) => [s.location, ...s.actors, ...s.props].some((r) => r && r.id === a.id && r.states.includes(st.id)));
    const speakers = allActions(doc).filter((x) => x.speaker?.id === a.id && x.speaker.state === st.id);
    const used = scenes.length + speakers.length;
    let rewired = "";
    if (used) {
        if (!opts.replacement && !opts.force) {
            throw new Error(`${a.name}/${st.name} is bound in ${scenes.length} scene(s)`
                + ` and named by ${speakers.length} speaker(s) — pass --replacement <state> to rewire them,`
                + ` or --force to drop the bindings`);
        }
        let to;
        if (opts.replacement) {
            const hit = a.states.filter((x) => x.id === opts.replacement || x.name === opts.replacement);
            if (hit.length !== 1)
                throw new Error(`${a.name} has no single state "${opts.replacement}"`);
            if (hit[0].id === st.id)
                throw new Error(`replacement must differ from the state being deleted`);
            to = hit[0].id;
        }
        for (const s of scenes) {
            for (const r of [s.raw.location, ...(s.raw.actors ?? []), ...(s.raw.props ?? [])]) {
                if (!r || r[`${a.kind}_id`] !== a.id)
                    continue;
                if (doc.version === 4) {
                    const next = new Set(r.state_ids.filter((x) => x !== st.id));
                    if (to)
                        next.add(to);
                    if (!next.size)
                        next.add("default");
                    r.state_ids = [...next].sort();
                }
                else
                    r[`state_id`] = to ?? "default";
            }
        }
        for (const x of speakers) {
            if (to)
                x.raw.speaker.state_id = to;
            else
                delete x.raw.speaker.state_id;
        }
        rewired = `\n${to ? `rewired` : `cleared`} ${scenes.length} scene binding(s)`
            + ` and ${speakers.length} speaker(s)${to ? ` to ${stateName(doc, a.id, to)}` : ""}`;
    }
    const list = a.raw.states;
    list.splice(list.findIndex((x) => x.state_id === st.id), 1);
    step("del", `state ${a.id}/${st.id}`);
    save(doc);
    return `deleted state ${a.id}/${st.id} ${st.name}` + rewired;
}
/**
 * Fold one asset into another: every scene reference and speaker moves to the
 * target, then the source is removed. States are matched by name where the
 * target has one, and otherwise carried over.
 */
export function merge(doc, srcAddr, dstAddr) {
    const s = resolve(doc, srcAddr), d = resolve(doc, dstAddr);
    if (s.t !== "asset" || d.t !== "asset")
        throw new Error(`merge takes two assets`);
    if (s.asset === d.asset)
        throw new Error(`cannot merge ${s.asset.name} into itself`);
    if (s.asset.kind !== d.asset.kind) {
        throw new Error(`cannot merge a ${s.asset.kind} into a ${d.asset.kind}`);
    }
    const base = baseline(doc);
    const src = s.asset, dst = d.asset;
    const kindKey = `${src.kind}_id`;
    // Map every source state onto a target state: same name if the target has it,
    // otherwise copy the definition across under a fresh id.
    const taken = new Set(dst.states.map((x) => x.id));
    const map = new Map();
    for (const st of src.states) {
        const same = dst.states.find((x) => x.name === st.name);
        if (same) {
            map.set(st.id, same.id);
            continue;
        }
        if (st.id === "default") {
            map.set(st.id, "default");
            continue;
        }
        const id = doc.version === 4 ? newId("st", taken) : `st_${String(dst.states.length + map.size).padStart(3, "0")}`;
        dst.raw.states.push({ state_id: id, state_name: st.name, description: st.desc });
        map.set(st.id, id);
    }
    const { scenes, speakers } = assetUses(doc, src.id);
    for (const scene of scenes) {
        for (const field of ["location", "actors", "props"]) {
            const slot = scene.raw[field];
            const list = field === "location" ? (slot ? [slot] : []) : (slot ?? []);
            for (const r of list) {
                if (r[kindKey] !== src.id)
                    continue;
                r[kindKey] = dst.id;
                if (doc.version === 4) {
                    r.state_ids = [...new Set(r.state_ids.map((x) => map.get(x) ?? "default"))].sort();
                }
                else
                    r.state_id = map.get(r.state_id) ?? "default";
            }
            // The target may already be referenced in this scene; one asset, one entry.
            if (field !== "location" && Array.isArray(slot)) {
                const merged = [];
                for (const r of slot) {
                    const prev = merged.find((x) => x[kindKey] === r[kindKey]);
                    if (!prev) {
                        merged.push(r);
                        continue;
                    }
                    if (doc.version === 4) {
                        prev.state_ids = [...new Set([...prev.state_ids, ...r.state_ids])].sort();
                    }
                }
                scene.raw[field] = merged;
            }
        }
    }
    for (const a of speakers) {
        a.raw.speaker.actor_id = dst.id;
        if (a.raw.speaker.state_id)
            a.raw.speaker.state_id = map.get(a.raw.speaker.state_id) ?? "default";
    }
    const list = doc.raw[ASSET_FIELD[src.kind]];
    list.splice(list.indexOf(src.raw), 1);
    step("merge", `${src.id} -> ${dst.id}: ${scenes.length} scenes, ${speakers.length} speakers`);
    save(doc);
    return `merged ${src.id} ${src.name} into ${dst.id} ${dst.name}`
        + `\nmoved ${scenes.length} scene reference(s), ${speakers.length} speaker(s),`
        + ` ${[...map].filter(([k, v]) => k !== v).length} state mapping(s)` + since(doc, base);
}
